import { useCallback, useEffect, useRef } from 'react';
import { type UserSubscription } from '@cx-sdk/subs';
import wretch from 'wretch';

import { type Repub } from '@cx-modules/constants';

import { type CXLoginSuccessEvent } from '~/custom-events/CXLoginSuccessEvent';
import { type SubsProduct } from '~/types/subs-product';
import { retryOperation } from '~/utils/retryOperation';
import { type SubscriptionTerm } from '~/utils/stripeProductValidators';

export type Selection = {
	product: number;
	term: SubscriptionTerm;
};

export const useSubscribe = ({
	cancelPayment,
	onCancel,
	onConfirmed,
	onError,
	onSubscribe,
	onVerifying,
	redirectPath,
	repub,
	shouldLogin,
	trialDays,
	userSubscriptions,
	verifyPayment,
}: {
	cancelPayment: string | null;
	onCancel?: () => void;
	onConfirmed?: () => void;
	onError?: () => void;
	onSubscribe: (
		product: SubsProduct,
		checkout: (product: SubsProduct) => void,
	) => void;
	onVerifying?: () => void;
	redirectPath: string | null;
	repub: Repub;
	shouldLogin: boolean;
	trialDays?: number;
	userSubscriptions: UserSubscription[];
	verifyPayment: string | null;
}): {
	getPortalUrl: () => {};
	subscribe: (
		product: SubsProduct,
		userData?: CXLoginSuccessEvent['detail'],
	) => void;
} => {
	const verifyRef = useRef(false);
	const abortRef = useRef(false);

	/**
	 * Ref to ensure we only call `getCheckoutUrl` once at a time
	 * so that we don't trigger multiple competing fetches that
	 * can lead to one being cancelled.
	 */
	const checkoutInFlightRef = useRef(false);

	const getCheckoutUrl = useCallback(
		async (
			{ id: priceId, priceFormatted, term, type }: SubsProduct,
			userData?: CXLoginSuccessEvent['detail'],
		) => {
			const encodedPrice = encodeURIComponent(priceFormatted);

			const successUrl = new URL(window.location.href);
			successUrl.searchParams.set('verifyPayment', 'true');
			successUrl.searchParams.set('price', encodedPrice);
			if (term) {
				successUrl.searchParams.set('term', term);
			}
			if (redirectPath) {
				successUrl.searchParams.set('redirectTo', redirectPath);
			}

			const cancelUrl = new URL(window.location.href);
			cancelUrl.searchParams.set('cancelledPayment', 'true');
			if (redirectPath) {
				cancelUrl.searchParams.set('redirectTo', redirectPath);
			}

			const userHasCancelledSubscription =
				(userData?.subscriptions ?? userSubscriptions).some(
					({ status }) => status === 'CANCELLED',
				) ?? false;

			return await wretch('/api/cx/subscriptions/checkout')
				.post({
					priceId,
					type,
					urls: {
						successUrl: successUrl.toString(),
						cancelUrl: cancelUrl.toString(),
					},
					...(userHasCancelledSubscription ? {} : { trialDays }),
					...(userData
						? { authToken: userData.token, deviceId: userData.deviceId }
						: {}),
				})
				.json<string>();
		},
		[redirectPath, trialDays, userSubscriptions],
	);

	const getPortalUrl = useCallback(async () => {
		const returnUrl = new URL(window.location.href);
		return await wretch('/api/cx/subscriptions/portal')
			.post({ returnUrl: returnUrl.toString() })
			.json<string>();
	}, []);

	const checkout = useCallback(
		async (product: SubsProduct, userData?: CXLoginSuccessEvent['detail']) => {
			// Prevent multiple calls from racing (double-click or re-render)
			if (checkoutInFlightRef.current) {
				return;
			}
			checkoutInFlightRef.current = true;

			try {
				const checkoutUrl = await getCheckoutUrl(product, userData);
				if (checkoutUrl) {
					// Redirect to checkout
					window.location.href = checkoutUrl;
				} else {
					// If no url we need to redirect back to where the user came from (redirectPath)
					const urlOrigin = window.location.origin;
					window.location.href = `${urlOrigin}/${redirectPath ?? ''}`;
				}
			} catch (err) {
				// If there's an error or the fetch is cancelled, call onError so the UI can reset
				onError?.();
			} finally {
				checkoutInFlightRef.current = false;
			}
		},
		[getCheckoutUrl, onError, redirectPath],
	);

	const subscribe = useCallback(
		async (product: SubsProduct, userData?: CXLoginSuccessEvent['detail']) => {
			onSubscribe(product, (p: SubsProduct) => checkout(p, userData));
		},
		[checkout, onSubscribe],
	);

	const verify = useCallback(async () => {
		onVerifying?.();

		const confirmSubscription = async () => {
			const subscriptions = await wretch('/api/cx/subscriptions')
				.get()
				.json<UserSubscription[]>();
			return subscriptions.length > 0;
		};

		try {
			const isSubscribed = await retryOperation(
				confirmSubscription,
				1000,
				10,
				abortRef,
			);

			if (!isSubscribed) {
				throw new Error('User is not subscribed');
			}

			try {
				// Fire and forget to trigger cache invalidation
				const headers = new Headers();
				headers.append('OC-Repub', repub);

				await wretch()
					.url(`/api/cx/cvms/v1/visitor-walls`)
					.headers(headers)
					.get()
					.res();
			} catch {
				// no-op
			}

			onConfirmed?.();
		} catch {
			onError?.();
		}
	}, [onConfirmed, onError, onVerifying, repub]);

	const cancel = useCallback(() => {
		onCancel?.();
	}, [onCancel]);

	// Construct flow helper conditionals
	const shouldVerify = verifyPayment && !shouldLogin;
	const shouldCancel = cancelPayment && !shouldLogin;

	// Trigger verification flow after stripe
	useEffect(() => {
		if (verifyRef.current) return;
		if (!shouldVerify) return;

		verify();
		// Only set up the abort for the verifying flow
		return () => {
			verifyRef.current = true;
			abortRef.current = true;
		};
	}, [shouldVerify, verify]);

	// Trigger failure flow after stripe
	useEffect(() => {
		if (!shouldCancel) return;
		cancel();
	}, [shouldCancel, cancel]);

	return {
		getPortalUrl,
		subscribe,
	};
};
